import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Drawer, Input, List, Popover, Tooltip } from 'antd';
import { DownOutlined, LeftOutlined, RightOutlined, HolderOutlined, MoreOutlined, QuestionCircleOutlined, CloseOutlined } from '@ant-design/icons';
import './LegendDrawer.scss';
import LayerOptions from './LayerOption';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { Sketch } from '../../../models/sketch.model';
import { useSketchLogic } from '../../../hooks/useSketchLogic';
import SketchList from './Sketches/SketchList';
import SketchDrawerContent from './Sketches/SketchDrawerContent';
import LayerPropertiesModal from '../LayerProperties/LayerPropertiesModal';
import CreateGroupModal from './CreateGroupModal';

// Modals
import ViewAttributeTable from './ViewAttributeTable';
import SaveLayerModal from './SaveLayerModal';
import DuplicateLayerModal, { DuplicateMode } from './DuplicateLayerModal';
import DownloadDatasetModal from './DownloadDatasetModal';
import useDownload, { ExportFormat } from '../../../hooks/useDownload';
import { Modal } from 'antd';
import LegendLayerSymbology from './LegendLayerSymbology';
import { SymbologyConfig } from '../../../types/symbology.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useAuth } from '../../../context/AuthContext';
import { LayerAction } from '../../../enums/mapPortalActions';
import { layerActionAccess } from '../constants';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import Notification from '../../../shared/components/Notification';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { QueryKeys } from '../../../enums/queryKeys';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter, DragEndEvent, useDroppable } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// US-101358/101359: thin wrapper that supplies dnd-kit sortable refs to the
// row body and exposes the drag listeners so they can be attached to the grip
// handle only (the rest of the row stays clickable for visibility toggles,
// menus, etc.).
interface SortableSlotProps {
  id: string;
  disabled?: boolean;
  children: (drag: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
    isOver: boolean;
    isDragging: boolean;
  }) => React.ReactNode;
}

const SortableSlot: React.FC<SortableSlotProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id,
    disabled,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes, isOver, isDragging })}
    </div>
  );
};

// US-101358 AC-03: empty containers (an empty group's children section, or an
// empty ungrouped section) need an explicit droppable so a layer can be moved
// into a container that currently has no rows to drop onto.
const DropZone: React.FC<{ id: string; className?: string; children?: React.ReactNode }> = ({ id, className, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`${className ?? ''}${isOver ? ' is-drop-target' : ''}`.trim()}>
      {children}
    </div>
  );
};

export interface DrawerItem {
  id: number;
  uniqueLayerName: string;
  toggled?: boolean;
  downloadable?: boolean;
  isHidden?: boolean;
  displayOnMap?: boolean;
  displayInLegend?: boolean;
  folderId: string;
  s3Key: string;
  layerTitle?: string;
  layerType?: string;
  layerDescription?: string;
  symbology?: string;
  symbologyConfig?: SymbologyConfig | null;
  groupId?: number | null;
  sortOrder?: number;
  // Ingestion lifecycle. "processing" = still publishing (shown disabled, not
  // interactive); "ready"/null = usable. ("failed" is filtered out server-side.)
  publishStatus?: 'processing' | 'ready' | 'failed' | null;
}

export interface LayerGroup {
  id: number;
  name: string;
}

interface CustomDrawerProps {
  drawerItems: DrawerItem[];
  folderId: string;
  mapId: string;
  setDrawerItems: React.Dispatch<React.SetStateAction<any[]>>;
  setBoundingLayers: React.Dispatch<React.SetStateAction<any>>;
  viewerRef: any;
  onLayerUpdateComplete?: () => Promise<void>;
  setIsLayerLoading: (loading: boolean) => void;
  isPrintView?: boolean;
}

const LegendDrawer: React.FC<CustomDrawerProps> = ({
  drawerItems,
  setDrawerItems,
  setBoundingLayers,
  mapId,
  viewerRef,
  onLayerUpdateComplete,
  setIsLayerLoading,
  isPrintView = false,
}) => {
  const [selectedLayerName, setSelectedLayerName] = useState<string>('');
  const [selectedLayerTitle, setSelectedLayerTitle] = useState<string>('');
  const [sketchFormMode, setSketchFormMode] = useState<'create' | 'edit'>('create');
  const sketchForm = useForm<Pick<Sketch, 'title' | 'description'>>({
    mode: 'onSubmit',
    defaultValues: { title: 'Untitled Sketch', description: '' },
  });

  const {
    open,
    setOpen,
    selectedOption,
    setSelectedOption,
    setModalVisible,
    attributeModalVisible,
    setAttributeModalVisible,
    saveLayerModalVisible,
    setSaveLayerModalVisible,
    sketchDrawerVisible,
    setSketchDrawerVisible,
    showHiddenLayers,
    setShowHiddenLayers,
  } = useLegendUI();

  const { sketchDrawerState, setSketchDrawerState, selectedSketch, setSelectedSketch, setSelectedSketchItem, selectedSketchItem } = useLegendForm();

  const { getLayerBounds, createDuplicateLayer, editLayerOptions, getLayerGroups, renameLayerGroup, ungroupLayerGroup, reorderLegend } =
    MapPortalService();
  const { mutateAsync: getLayerBoundsAsync } = useMutation(getLayerBounds());
  const { mutateAsync: createDuplicateLayerAsync, isPending: duplicatePending } = useMutation(createDuplicateLayer());
  // US-101365: state for the Duplicate Layer choice modal. Opens with the
  // target unique_layer_name when the user picks "Duplicate Layer" from
  // the layer options menu; the actual mutation fires on Create.
  const [duplicateModalLayer, setDuplicateModalLayer] = useState<string | null>(null);
  // Holds the layer whose Download modal is open. The whole item is kept so the
  // modal can title itself with layerTitle while exporting by uniqueLayerName.
  const [downloadLayer, setDownloadLayer] = useState<DrawerItem | null>(null);
  const { handleAsyncExport } = useDownload();
  const { mutateAsync: editLayerOptionsAsync } = useMutation(editLayerOptions());
  const { mutateAsync: renameLayerGroupAsync } = useMutation(renameLayerGroup());
  const { mutateAsync: ungroupLayerGroupAsync } = useMutation(ungroupLayerGroup());
  const { mutateAsync: reorderLegendAsync } = useMutation(reorderLegend());
  const queryClient = useQueryClient();
  // US-101358 AC-07: a small distance keeps clicks on the checkbox/menu from
  // accidentally triggering a drag. Keyboard sensor enables a11y reorder.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const numericMapId = mapId && mapId !== 'null' && !Number.isNaN(Number(mapId)) ? Number(mapId) : undefined;
  const { data: layerGroups = [], refetch: refetchLayerGroups } = useQuery({
    ...getLayerGroups(numericMapId),
    enabled: !!numericMapId && !isPrintView,
  });

  const { canAccess } = useAuth();
  const canReorderLayers = canAccess(layerActionAccess(LayerAction.REORDER_LAYERS));
  const canManageGroups = canAccess(layerActionAccess(LayerAction.MANAGE_GROUPS));
  const canEditLayer = canAccess(layerActionAccess(LayerAction.EDIT_LAYER_PROPERTIES));

  // Layer-group UI state
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});
  // Per-layer "show symbology" expansion (collapsed by default — matches Figma).
  const [expandedLayers, setExpandedLayers] = useState<Record<number, boolean>>({});
  const toggleLayerExpanded = (layerId: number) => setExpandedLayers((prev) => ({ ...prev, [layerId]: !prev[layerId] }));
  const [renamingGroupId, setRenamingGroupId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [openGroupMenu, setOpenGroupMenu] = useState<number | null>(null);
  const [createGroupForLayer, setCreateGroupForLayer] = useState<DrawerItem | null>(null);

  const {
    sketches,
    setSketches,
    drawingMode,
    setDrawingMode,
    pointsRef,
    createNewSketchItem,
    handleSaveSketchItem,
    deleteItem,
    createMapSketch,
    editMapSketch,
    deleteSketch,
    pendingTextClick,
    confirmTextLabel,
    cancelTextLabel,
  } = useSketchLogic(viewerRef, mapId);

  const [textLabelInput, setTextLabelInput] = useState('');
  const handleConfirmTextLabel = () => {
    const trimmed = textLabelInput.trim();
    if (!trimmed) return;
    confirmTextLabel(trimmed);
    setTextLabelInput('');
  };
  const handleCancelTextLabel = () => {
    cancelTextLabel();
    setTextLabelInput('');
  };

  // --- HANDLERS ---
  const onClose = () => setOpen(false);

  const visibilityDebounceRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const renamingOriginalNameRef = useRef<string>('');
  useEffect(() => {
    const timers = visibilityDebounceRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  // US-101361: the legend checkbox controls layer visibility on the map
  // (display_on_map). It must NOT touch display_in_legend, which is the
  // separate "Hidden Layers" concept owned by Layer Properties (AC-03).
  //
  // Perf: handler memoized so React.memo-d row components downstream don't
  // re-render every time the legend re-renders for unrelated reasons (drag
  // hover, popover toggles, etc.).
  const setLayerDisplayOnMapLocal = useCallback(
    (layerId: number, checked: boolean) => {
      setDrawerItems((prevItems) => prevItems.map((item) => (item.id === layerId ? { ...item, displayOnMap: checked } : item)));
    },
    [setDrawerItems],
  );

  const handleLayerVisibility = useCallback(
    (item: DrawerItem, checked: boolean) => {
      if (!!item.displayOnMap === checked) return;
      setLayerDisplayOnMapLocal(item.id, checked);

      const existing = visibilityDebounceRef.current.get(item.id);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        editLayerOptionsAsync({
          mapId: Number(mapId),
          layerId: item.id,
          layerOptions: { displayOnMap: checked },
        });
        visibilityDebounceRef.current.delete(item.id);
      }, 500);

      visibilityDebounceRef.current.set(item.id, timer);
    },
    [setLayerDisplayOnMapLocal, editLayerOptionsAsync, mapId],
  );

  const toggleGroupExpanded = (groupId: number) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: prev[groupId] === undefined ? false : !prev[groupId],
    }));
  };

  // AC-101361-05: when a group is re-checked we must NOT re-enable layers
  // that were individually unchecked before the group was unchecked. We snapshot
  // each child's pre-uncheck visibility and restore from it on re-check.
  const [groupVisibilitySnapshots, setGroupVisibilitySnapshots] = useState<Record<number, Record<number, boolean>>>({});

  const handleGroupVisibility = (groupId: number, children: DrawerItem[], checked: boolean) => {
    if (checked) {
      const snapshot = groupVisibilitySnapshots[groupId];
      children.forEach((child) => {
        // If we have a snapshot, only restore children that were visible
        // before the group was unchecked. Otherwise default to visible.
        const target = snapshot ? !!snapshot[child.id] : true;
        if (!!child.displayOnMap !== target) handleLayerVisibility(child, target);
      });
      if (snapshot) {
        setGroupVisibilitySnapshots((prev) => {
          const { [groupId]: _, ...next } = prev;
          return next;
        });
      }
    } else {
      const snapshot: Record<number, boolean> = {};
      children.forEach((child) => {
        snapshot[child.id] = !!child.displayOnMap;
        if (child.displayOnMap) handleLayerVisibility(child, false);
      });
      setGroupVisibilitySnapshots((prev) => ({ ...prev, [groupId]: snapshot }));
    }
  };

  const handleOpenCreateGroup = (layer: DrawerItem) => setCreateGroupForLayer(layer);

  const handleCloseCreateGroup = () => setCreateGroupForLayer(null);

  const handleGroupCreated = async () => {
    await refetchLayerGroups();
    if (onLayerUpdateComplete) await onLayerUpdateComplete();
  };

  const isDuplicateGroupName = (name: string, excludeGroupId?: number) =>
    groups.some((g) => g.id !== excludeGroupId && g.name.trim().toLowerCase() === name.trim().toLowerCase());

  const startRenameGroup = (group: LayerGroup) => {
    renamingOriginalNameRef.current = group.name;
    setRenamingGroupId(group.id);
    setRenameValue(group.name);
  };

  const endRenamingGroup = () => {
    renamingOriginalNameRef.current = '';
    setRenamingGroupId(null);
  };

  const handleConfirmRename = async (groupId: number) => {
    // Commits on blur as well as Enter — no button gates this.
    if (!canManageGroups) {
      endRenamingGroup();
      return;
    }
    const name = renameValue.trim();
    if (!name || name === renamingOriginalNameRef.current) {
      endRenamingGroup();
      return;
    }
    if (isDuplicateGroupName(name, groupId)) {
      Notification({
        message: 'Group name must be unique',
        description: 'A group with this name already exists',
        type: NotificationTypes.ERROR,
      });
      return;
    }
    try {
      await renameLayerGroupAsync({ mapId: Number(mapId), groupId, name });
      await refetchLayerGroups();
      endRenamingGroup();
    } catch {
      // renameLayerGroup mutation already surfaces the server error via Notification.
      // Keep the rename input open so the user can pick a different name.
    }
  };

  const handleRenameFieldKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') return;

    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    endRenamingGroup();
  };

  const handleUngroup = async (groupId: number) => {
    if (!canManageGroups) return;
    await ungroupLayerGroupAsync({ mapId: Number(mapId), groupId });
    await refetchLayerGroups();
    if (onLayerUpdateComplete) await onLayerUpdateComplete();
  };

  const handleDownloadLayer = (format: ExportFormat) => {
    if (!downloadLayer) return;
    handleAsyncExport(downloadLayer.uniqueLayerName, format);
  };

  const handleZoomToLayer = async (option: string) => {
    const layer = drawerItems.find((item) => item.uniqueLayerName === option);
    const bounds = await getLayerBoundsAsync({
      layerName: layer?.uniqueLayerName!,
    });
    // US-101364 AC-03: if the layer has no features, GeoServer's bbox
    // returns 0,0,0,0 (the fetchBoundingBoxFromGeoserver fallback) or is
    // missing entirely. Surface that to the user instead of silently
    // doing nothing — Cesium's flyTo skips zero rects.
    const minX = parseFloat(bounds?.minX ?? bounds?.minx ?? 'NaN');
    const minY = parseFloat(bounds?.minY ?? bounds?.miny ?? 'NaN');
    const maxX = parseFloat(bounds?.maxX ?? bounds?.maxx ?? 'NaN');
    const maxY = parseFloat(bounds?.maxY ?? bounds?.maxy ?? 'NaN');
    const isEmpty = !bounds || [minX, minY, maxX, maxY].some((n) => Number.isNaN(n)) || (minX === 0 && minY === 0 && maxX === 0 && maxY === 0);

    if (isEmpty) {
      Notification({
        message: 'No features to zoom to in this layer.',
        type: NotificationTypes.INFO,
      });
      return;
    }

    setBoundingLayers(bounds);
  };

  const handleTableClick = (value: boolean, layerName: string, layerTitle: string) => {
    setAttributeModalVisible(value);
    setSelectedLayerName(layerName);
    setSelectedLayerTitle(layerTitle);
  };

  const handleSketchFormFinish = (values: Pick<Sketch, 'title' | 'description'>) => {
    const payloadBase = { ...values, mapId };
    if (sketchFormMode === 'create') {
      createMapSketch(payloadBase as Sketch).then((res) => {
        if (res) {
          setSelectedSketch(res);
          setSketches((prev) => [...prev, res]);
          setSketchDrawerState('view');
          setSketchFormMode('create');
          sketchForm.reset({ title: 'Untitled Sketch', description: '' });
        }
      });
      return;
    }

    if (sketchFormMode === 'edit' && selectedSketch) {
      const payload = {
        ...selectedSketch,
        ...payloadBase,
      };
      editMapSketch(payload).then((res) => {
        if (res) {
          setSelectedSketch(res);
          setSketches((prev) => prev.map((s) => (s.id === res.id ? res : s)));
          setSketchDrawerState('view');
        }
      });
    }
  };

  const handleAddSketch = () => {
    setSelectedSketch(null);
    setSelectedSketchItem(null);
    viewerRef.current?.entities.removeAll();
    viewerRef.current?.dataSources.removeAll();
    pointsRef.current = [];
    setDrawingMode('');
    sketchForm.reset({ title: 'Untitled Sketch', description: '' });
    setSketchFormMode('create');
    setSketchDrawerState('create');
    if (!sketchDrawerVisible) setSketchDrawerVisible(true);
  };

  const handleViewSketchProperties = (sketch: Sketch) => {
    setSelectedSketch(sketch);
    setSelectedSketchItem(null);
    setDrawingMode('');
    pointsRef.current = [];
    setSketchFormMode('edit');
    setSketchDrawerState('create');
    sketchForm.reset({
      title: sketch.title,
      description: sketch.description,
    });
    if (!sketchDrawerVisible) setSketchDrawerVisible(true);
  };

  // --- RENDER CONTENT ---
  const groups = layerGroups as LayerGroup[];
  const groupIdSet = useMemo(() => new Set(groups.map((g) => g.id)), [groups]);
  // US-101373 Scenarios 1 / 3: layers whose Display-in-Legend is OFF are
  // absent from the main LAYERS view by default. When the Editor/Admin ticks
  // "Show Hidden Layers" we *interleave* them at their natural position
  // (group + sort_order) rather than gathering them in a separate section.
  // Each hidden row carries a "Hidden Layer" tag in renderLayerRow.
  const includeHidden = canEditLayer && showHiddenLayers;
  // Perf: derived layer lists recomputed every render were dominating the
  // legend's render cost when drawerItems grew. Memoize keyed on the inputs
  // that actually matter (drawerItems identity, groupIdSet, includeHidden).
  const isVisibleInLegend = useCallback((l: DrawerItem) => l.displayInLegend !== false || includeHidden, [includeHidden]);
  const layersByGroup = useMemo(() => {
    const map = new Map<number, DrawerItem[]>();
    for (const l of drawerItems) {
      if (l.groupId == null || !groupIdSet.has(l.groupId)) continue;
      if (!isVisibleInLegend(l)) continue;
      const list = map.get(l.groupId) ?? [];
      list.push(l);
      map.set(l.groupId, list);
    }
    return map;
  }, [drawerItems, groupIdSet, isVisibleInLegend]);
  const layersInGroup = useCallback((groupId: number) => layersByGroup.get(groupId) ?? [], [layersByGroup]);
  const ungroupedLayers = useMemo(
    () => drawerItems.filter((l) => (!l.groupId || !groupIdSet.has(l.groupId)) && isVisibleInLegend(l)),
    [drawerItems, groupIdSet, isVisibleInLegend],
  );

  // US-101373 AC-02 / AC-05: a layer is "hidden" when its Layer Properties
  // "Display in Legend" toggle is OFF. The interleaved rendering uses
  // isVisibleInLegend (below) rather than a separate filtered array.

  // AC-04 / Scenario 9: showHiddenLayers is now read from LegendUIContext
  // (destructured at the top of this component) so it survives in-app
  // navigation. `pendingShowHidden` is the popover-local staged value — the
  // checkbox writes to it, OK commits, Cancel discards.
  const [hiddenPopoverOpen, setHiddenPopoverOpen] = useState(false);
  const [pendingShowHidden, setPendingShowHidden] = useState(false);
  useEffect(() => {
    if (hiddenPopoverOpen) setPendingShowHidden(showHiddenLayers);
  }, [hiddenPopoverOpen, showHiddenLayers]);

  useEffect(() => {
    // The "Hidden Layers" tab is gone; if a previous session left it as the
    // active value in context, snap back to Legend.
    if (selectedOption === 'Hidden Layers') setSelectedOption('Legend');
    // AC-03: Viewers must not be able to reveal hidden layers, even from a
    // stale toggle.
    if (!canEditLayer && showHiddenLayers) setShowHiddenLayers(false);
  }, [selectedOption, canEditLayer, showHiddenLayers, setSelectedOption]);

  // US-101358 / US-101359 — keep map rendering aligned with the *visual*
  // legend stack. sort_order is per-layer, so on initial load (or after data
  // refetches) layers in the topmost group are not guaranteed to sit above
  // layers in a lower group on the map. We re-emit drawerItems in the order
  // the user sees: each group walked in its sort_order, its children in
  // theirs, then the ungrouped section. drawerItems is then what feeds the
  // Cesium hook, which reverse-iterates so the topmost row ends on top.
  useEffect(() => {
    if (drawerItems.length === 0) return;
    const inGroup: DrawerItem[] = [];
    for (const g of groups) inGroup.push(...drawerItems.filter((l) => l.groupId === g.id));
    const ordered: DrawerItem[] = [...inGroup, ...ungroupedLayers];
    // The filter pair above may exclude layers whose groupId points at a
    // group that hasn't loaded yet — keep them at the end so nothing gets
    // dropped while data is in flight.
    const seen = new Set(ordered.map((l) => l.id));
    for (const l of drawerItems) if (!seen.has(l.id)) ordered.push(l);
    const sameOrder = ordered.length === drawerItems.length && ordered.every((l, i) => drawerItems[i]?.id === l.id);
    if (!sameOrder) setDrawerItems(ordered);
  }, [drawerItems, groups]);

  const groupChecked = (children: DrawerItem[]) => children.length > 0 && children.every((c) => c.displayOnMap);
  const groupIndeterminate = (children: DrawerItem[]) => children.some((c) => c.displayOnMap) && !children.every((c) => c.displayOnMap);

  // US-101358 / US-101359 — drag-and-drop ordering.
  //
  // sort_order behaves as a z-index: the layer (or group) with the highest
  // sort_order sits at the top of the legend AND renders on top of the map.
  // After a drop we rebuild the full flat legend top→bottom, then assign
  // sort_order = (length - 1 - index) so the topmost item gets the highest
  // value. The same array (already in top-of-legend-first order) feeds Cesium,
  // and the hook reverse-iterates so the topmost row ends on top of the map.
  //
  // Cross-container drops (US-101358 AC-03) are also handled here: a layer
  // dropped on a sibling in a different container, on a group header, or on
  // an empty container's drop zone updates its groupId in the same bulk PUT.
  const onLegendDragEnd = (e: DragEndEvent) => {
    // Single funnel for every reorder, pointer and keyboard sensor alike.
    if (!canReorderLayers) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith('group-') && overId.startsWith('group-')) {
      const activeGid = Number(activeId.slice('group-'.length));
      const overGid = Number(overId.slice('group-'.length));
      const oldIdx = groups.findIndex((g) => g.id === activeGid);
      const newIdx = groups.findIndex((g) => g.id === overGid);
      if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
      const newGroups = arrayMove([...groups], oldIdx, newIdx);
      // Flat layer order follows the new group order.
      const newFlat: DrawerItem[] = [
        ...newGroups.flatMap((g) => drawerItems.filter((l) => l.groupId === g.id)),
        ...drawerItems.filter((l) => !l.groupId || !groupIdSet.has(l.groupId)),
      ];
      // z-index assignment: top of the legend (index 0) gets the highest
      // sort_order, last item gets 0.
      const groupPayload = newGroups.map((g, i) => ({
        id: g.id,
        sortOrder: newGroups.length - 1 - i,
      }));
      const layerPayload = newFlat.map((l, i) => ({
        id: l.id,
        sortOrder: newFlat.length - 1 - i,
      }));
      // Optimistic local
      queryClient.setQueryData([QueryKeys.GET_LAYER_GROUPS, numericMapId], newGroups);
      setDrawerItems(newFlat.map((l, i) => ({ ...l, sortOrder: newFlat.length - 1 - i })));
      if (numericMapId) {
        reorderLegendAsync({ mapId: numericMapId, groups: groupPayload, layers: layerPayload });
      }
      return;
    }

    if (activeId.startsWith('layer-')) {
      const activeLid = Number(activeId.slice('layer-'.length));
      const activeLayer = drawerItems.find((l) => l.id === activeLid);
      if (!activeLayer) return;

      // Figure out where this layer is being dropped.
      //  - over a sibling layer:   land at that layer's index, in that layer's container.
      //  - over a group header:    append to the bottom of that group's children.
      //  - over a container slot:  the empty drop zones we render for empty
      //                            groups / ungrouped section.
      let targetGroupId: number | null;
      let targetIndex: number;

      if (overId.startsWith('layer-')) {
        const overLid = Number(overId.slice('layer-'.length));
        const overLayer = drawerItems.find((l) => l.id === overLid);
        if (!overLayer) return;
        targetGroupId = overLayer.groupId ?? null;
        const targetContainer = drawerItems.filter((l) => (l.groupId ?? null) === targetGroupId && l.id !== activeLid);
        targetIndex = targetContainer.findIndex((l) => l.id === overLid);
        if (targetIndex < 0) targetIndex = targetContainer.length;
      } else if (overId.startsWith('group-')) {
        targetGroupId = Number(overId.slice('group-'.length));
        targetIndex = drawerItems.filter((l) => l.groupId === targetGroupId && l.id !== activeLid).length; // append
      } else if (overId.startsWith('container-group-')) {
        targetGroupId = Number(overId.slice('container-group-'.length));
        targetIndex = drawerItems.filter((l) => l.groupId === targetGroupId && l.id !== activeLid).length;
      } else if (overId === 'container-ungrouped') {
        targetGroupId = null;
        targetIndex = drawerItems.filter((l) => (!l.groupId || !groupIdSet.has(l.groupId)) && l.id !== activeLid).length;
      } else {
        return;
      }

      const sourceGroupId = activeLayer.groupId ?? null;
      const isSameContainer = sourceGroupId === targetGroupId;

      // Build the target container with the active layer inserted, then
      // rebuild the full flat legend top→bottom. We always re-emit the layer
      // with its (possibly new) groupId so the backend persists the move.
      const without = drawerItems.filter((l) => l.id !== activeLid);
      const targetContainerWithoutActive = without.filter((l) => (l.groupId ?? null) === targetGroupId);
      const inserted: DrawerItem[] = [...targetContainerWithoutActive];
      inserted.splice(targetIndex, 0, { ...activeLayer, groupId: targetGroupId });

      // No-op early exit when the drop didn't actually move the layer.
      if (isSameContainer) {
        const original = drawerItems.filter((l) => (l.groupId ?? null) === sourceGroupId);
        const originalIdx = original.findIndex((l) => l.id === activeLid);
        if (originalIdx === targetIndex) return;
      }

      const newFlat: DrawerItem[] = [];
      for (const g of groups) {
        if (g.id === targetGroupId) newFlat.push(...inserted);
        else newFlat.push(...without.filter((l) => l.groupId === g.id));
      }
      if (targetGroupId === null) newFlat.push(...inserted);
      else newFlat.push(...without.filter((l) => !l.groupId || !groupIdSet.has(l.groupId)));

      const len = newFlat.length;
      const layerPayload = newFlat.map((l, i) => ({
        id: l.id,
        sortOrder: len - 1 - i,
        // Always send groupId — cheap, and makes cross-container moves a
        // single round-trip alongside the sort_order rewrite.
        groupId: l.groupId ?? null,
      }));
      setDrawerItems(newFlat.map((l, i) => ({ ...l, sortOrder: len - 1 - i })));
      if (numericMapId) reorderLegendAsync({ mapId: numericMapId, layers: layerPayload });
    }
  };

  const renderLayerRow = (item: DrawerItem, nested = false) => {
    // US-101373 Scenarios 3 / 6: a layer with displayInLegend === false is
    // a "Hidden Layer". It is shown inline only when the Editor toggle is
    // on, carries an eye-off icon + "Hidden Layer" tag, and is not
    // draggable from this view (admin un-hide happens via Layer Properties;
    // hidden-row reordering is its own story, US-101375).
    const isHidden = item.displayInLegend === false;
    // Still publishing: render the row disabled and inert. No visibility toggle,
    // no symbology expand, no options menu — nothing fetches or touches the
    // layer until it's "ready". (Backend already excludes "failed".)
    const isProcessing = item.publishStatus === 'processing';
    // Vector layers can show their applied symbology; raster/WMS cannot.
    const canShowSymbology = !['raster', 'WMS'].includes(item.layerType || '') && !isProcessing;
    const expanded = !!expandedLayers[item.id];
    return (
      <SortableSlot key={`layer-${item.id}`} id={`layer-${item.id}`} disabled={!canReorderLayers || isPrintView || isProcessing}>
        {({ listeners, attributes, isOver }) => (
          <div className="legend-layer">
            <div
              className={`legend-row legend-row--layer${nested ? ' legend-row--nested' : ''}${isOver ? ' is-drop-target' : ''}${isHidden ? ' legend-row--hidden' : ''}${isProcessing ? ' legend-row--processing' : ''}`}
            >
              {/* US-101375: hidden rows are now draggable too. The "Hidden
                  Layer" tag inside the title is enough of a visual indicator,
                  so we drop the dedicated eye-off icon. */}
              <ConditionalRender visible={canReorderLayers && !isPrintView && !isProcessing}>
                <span className="legend-row__grip" aria-label="Drag to reorder" {...attributes} {...listeners}>
                  <HolderOutlined />
                </span>
              </ConditionalRender>
              <Checkbox
                checked={!isProcessing && !!item.displayOnMap}
                disabled={isProcessing}
                onChange={(e) => handleLayerVisibility(item, e.target.checked)}
              />
              {/* Expand chevron: reveals the layer's applied symbology
                  (icons + labels). Hidden for raster/WMS and processing layers. */}
              {canShowSymbology ? (
                <button
                  type="button"
                  className="legend-row__chevron"
                  onClick={() => toggleLayerExpanded(item.id)}
                  aria-label={expanded ? 'Hide symbology' : 'Show symbology'}
                  aria-expanded={expanded}
                >
                  {expanded ? <DownOutlined /> : <LeftOutlined />}
                </button>
              ) : (
                <span className="legend-row__chevron legend-row__chevron--placeholder" />
              )}
              <span className="legend-row__title" title={item.layerTitle || item.uniqueLayerName}>
                {item.layerTitle || item.uniqueLayerName}
                {isHidden && <span className="legend-row__hidden-tag">Hidden Layer</span>}
                {isProcessing && <span className="legend-row__processing-tag">Processing…</span>}
              </span>
              {!isPrintView && !isProcessing && (
                <LayerOptions
                  item={item}
                  onZoomToLayer={handleZoomToLayer}
                  setModalVisible={setModalVisible}
                  setAttributeModalVisible={setAttributeModalVisible}
                  onDuplicateLayer={(layerName) => setDuplicateModalLayer(layerName)}
                  onDownloadLayer={setDownloadLayer}
                  onSaveLayer={setSaveLayerModalVisible}
                  mapId={mapId}
                  handleTableClick={handleTableClick}
                  setIsLayerLoading={setIsLayerLoading}
                  onLayerUpdateComplete={onLayerUpdateComplete}
                  onCreateGroup={() => handleOpenCreateGroup(item)}
                />
              )}
            </div>
            {canShowSymbology && expanded && (
              <div className={`legend-layer__symbology${nested ? ' legend-layer__symbology--nested' : ''}`}>
                <LegendLayerSymbology layer={item} />
              </div>
            )}
          </div>
        )}
      </SortableSlot>
    );
  };

  const renderGroup = (group: LayerGroup) => {
    const children = layersInGroup(group.id);
    const expanded = expandedGroups[group.id] !== false; // default expanded
    return (
      <SortableSlot key={`group-${group.id}`} id={`group-${group.id}`} disabled={!canReorderLayers || isPrintView}>
        {({ listeners, attributes, isOver }) => (
          <div className="legend-group">
            <div className={`legend-row legend-row--group${isOver ? ' is-drop-target' : ''}`}>
              <ConditionalRender visible={canReorderLayers && !isPrintView}>
                <span className="legend-row__grip" aria-label="Drag to reorder" {...attributes} {...listeners}>
                  <HolderOutlined />
                </span>
              </ConditionalRender>
              <Checkbox
                checked={groupChecked(children)}
                indeterminate={groupIndeterminate(children)}
                onChange={(e) => handleGroupVisibility(group.id, children, e.target.checked)}
              />
              <button
                type="button"
                className="legend-group__chevron"
                onClick={() => toggleGroupExpanded(group.id)}
                aria-label={expanded ? 'Collapse group' : 'Expand group'}
              >
                {expanded ? <DownOutlined /> : <RightOutlined />}
              </button>
              {renamingGroupId === group.id ? (
                <Input
                  size="small"
                  autoFocus
                  className="legend-group__rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onPressEnter={() => handleConfirmRename(group.id)}
                  onBlur={() => handleConfirmRename(group.id)}
                  onKeyDown={handleRenameFieldKeydown}
                />
              ) : (
                <span className="legend-group__name" title={group.name} onDoubleClick={() => canManageGroups && startRenameGroup(group)}>
                  {group.name}
                </span>
              )}
              <ConditionalRender visible={!isPrintView && canManageGroups}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  overlayClassName="layer-options__popover"
                  open={openGroupMenu === group.id}
                  onOpenChange={(v) => setOpenGroupMenu(v ? group.id : null)}
                  content={
                    <List
                      size="small"
                      className="layer-options__list"
                      dataSource={[
                        {
                          label: 'Rename Group',
                          action: () => {
                            setOpenGroupMenu(null);
                            startRenameGroup(group);
                          },
                        },
                        {
                          label: 'Ungroup',
                          action: () => {
                            setOpenGroupMenu(null);
                            handleUngroup(group.id);
                          },
                        },
                      ]}
                      renderItem={(opt) => (
                        <List.Item className="layer-options__item" onClick={opt.action}>
                          {opt.label}
                        </List.Item>
                      )}
                    />
                  }
                >
                  <Button className="legend-row__options-trigger" type="text">
                    <MoreOutlined />
                  </Button>
                </Popover>
              </ConditionalRender>
            </div>
            {expanded && (
              <div className="legend-group__children">
                {children.length === 0 ? (
                  <DropZone id={`container-group-${group.id}`} className="legend-group__empty">
                    Drop a layer here to add it to this group
                  </DropZone>
                ) : (
                  <SortableContext items={children.map((c) => `layer-${c.id}`)} strategy={verticalListSortingStrategy}>
                    {children.map((child) => renderLayerRow(child, true))}
                  </SortableContext>
                )}
              </div>
            )}
          </div>
        )}
      </SortableSlot>
    );
  };

  const renderDrawerContent = () => {
    if (selectedOption === 'Sketches') {
      return (
        <SketchList
          sketches={sketches}
          onToggleVisible={(sketch, checked) => {
            editMapSketch({ ...sketch, visible: checked, mapId });
            setSketches((prev) => prev.map((s) => (s.id === sketch.id ? { ...s, visible: checked } : s)));
          }}
          onDeleteSketch={deleteSketch}
          onAddSketch={handleAddSketch}
          onViewSketchProperties={handleViewSketchProperties}
          viewerRef={viewerRef}
          pointsRef={pointsRef}
        />
      );
    }

    return (
      <div className="legend-panel__list">
        {/* US-101373 Scenario 3: hidden layers are now interleaved with the
            regular legend (each carries a "Hidden Layer" tag via
            renderLayerRow) — no separate section. */}
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onLegendDragEnd}>
          <SortableContext items={groups.map((g) => `group-${g.id}`)} strategy={verticalListSortingStrategy}>
            {groups.map((group) => renderGroup(group))}
          </SortableContext>
          {ungroupedLayers.length > 0 && (
            <SortableContext items={ungroupedLayers.map((l) => `layer-${l.id}`)} strategy={verticalListSortingStrategy}>
              {ungroupedLayers.map((item) => renderLayerRow(item))}
            </SortableContext>
          )}
          {/* US-101358 AC-03 / US-101375 Scenario 2: explicit drop target for
              the ungrouped section. Rendered whenever the user has groups, so
              a layer (visible OR hidden) can be moved *out* of a group even
              when the only siblings in the ungrouped section don't accept
              that kind of drop (e.g. a hidden drag with only visible
              ungrouped siblings). Slim when the section already has content. */}
          <ConditionalRender visible={canReorderLayers && groups.length > 0}>
            <DropZone
              id="container-ungrouped"
              className={`legend-panel__ungrouped-empty${ungroupedLayers.length > 0 ? ' legend-panel__ungrouped-empty--slim' : ''}`}
            >
              {ungroupedLayers.length === 0 ? 'Drop a layer here to move it out of its group' : null}
            </DropZone>
          </ConditionalRender>
        </DndContext>
        {groups.length === 0 && drawerItems.length === 0 && <div className="legend-panel__empty">No layers added yet</div>}
      </div>
    );
  };

  return (
    <>
      <Button
        className="legend-drawer__toggle"
        style={{
          position: 'absolute',
          right: open ? 420 : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
        }}
        onClick={() => setOpen(!open)}
      >
        <div className="legend-drawer__toggle-icon">{open ? <RightOutlined /> : <LeftOutlined />}</div>
      </Button>

      {!open && <div className="legend-drawer__closed-divider" />}

      <Drawer
        onClose={onClose}
        open={open}
        placement="right"
        getContainer={false}
        closeIcon={false}
        mask={false}
        width={420}
        className="legend-drawer__panel"
        rootClassName="legend-drawer__drawer-root"
        styles={{ body: { padding: 0 } }}
        // Height fits the legend content (capped) instead of a fixed 60%, so
        // the empty area below the list no longer blocks map clicks.
        // style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', height: 'auto', maxHeight: '85vh' }}
      >
        <div className="legend-panel__header">
          <span className="legend-panel__title">LEGEND</span>
          <span className="legend-panel__header-icons">
            {/* US-101373 AC-04: clicking the layers-stack icon opens a popover
                with a "Show Hidden Layers" toggle. Editors and Admins only —
                AC-03 keeps Viewers from seeing it at all. */}
            {canEditLayer ? (
              <Popover
                trigger="click"
                placement="bottomRight"
                open={hiddenPopoverOpen}
                onOpenChange={setHiddenPopoverOpen}
                overlayClassName="legend-panel__hidden-popover-wrap"
                content={
                  // Scenario 7: the popover stages its checkbox value
                  // locally; clicking Cancel (or dismissing by clicking
                  // outside) leaves showHiddenLayers untouched. OK commits
                  // and closes.
                  <div className="legend-panel__hidden-popover">
                    <Checkbox checked={pendingShowHidden} onChange={(e) => setPendingShowHidden(e.target.checked)}>
                      Show Hidden Layers
                    </Checkbox>
                    <div className="legend-panel__hidden-popover-actions">
                      <Button size="small" onClick={() => setHiddenPopoverOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                          setShowHiddenLayers(pendingShowHidden);
                          setHiddenPopoverOpen(false);
                        }}
                      >
                        OK
                      </Button>
                    </div>
                  </div>
                }
              >
                <button type="button" className="legend-panel__stack-icon legend-panel__stack-icon--button" aria-label="Show hidden layers">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 12 12 17 22 12" />
                    <polyline points="2 17 12 22 22 17" />
                  </svg>
                </button>
              </Popover>
            ) : (
              <span className="legend-panel__stack-icon" aria-hidden>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 12 12 17 22 12" />
                  <polyline points="2 17 12 22 22 17" />
                </svg>
              </span>
            )}
            <Tooltip
              placement="bottomRight"
              overlayClassName="legend-panel__help-tip"
              title={
                <div className="legend-panel__help">
                  <strong>How to use:</strong>
                  <ul>
                    <li>Click checkboxes to toggle groups/layers on/off</li>
                    <li>Click chevron icons to expand/collapse groups</li>
                    <li>Drag the grip icon to reorder groups and layers</li>
                    <li>Drop layers into different groups to reorganize</li>
                    <li>Symbology icons show the map representation</li>
                  </ul>
                </div>
              }
            >
              <QuestionCircleOutlined className="legend-panel__help-icon" />
            </Tooltip>
          </span>
        </div>

        {!isPrintView && (
          <div className="legend-panel__tabs">
            <button
              type="button"
              className={`legend-panel__tab${selectedOption === 'Legend' ? ' legend-panel__tab--active' : ''}`}
              onClick={() => setSelectedOption('Legend')}
            >
              LAYERS
            </button>
            <button
              type="button"
              className={`legend-panel__tab${selectedOption === 'Sketches' ? ' legend-panel__tab--active' : ''}`}
              onClick={() => setSelectedOption('Sketches')}
            >
              SKETCHES
            </button>
          </div>
        )}

        <div className="legend-panel__body">{renderDrawerContent()}</div>
      </Drawer>

      <Drawer
        onClose={() => setSketchDrawerVisible(false)}
        open={sketchDrawerVisible}
        placement="left"
        getContainer={false}
        closeIcon={false}
        width={410}
        rootClassName="legend-sketch-drawer__drawer-root"
        destroyOnClose={true}
        mask={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="legend-panel__header">
          <span className="legend-panel__title">{sketchFormMode === 'create' ? 'Untitled Sketch' : 'Edit Sketch'}</span>
          <button type="button" className="legend-panel__header-close" aria-label="Close" onClick={() => setSketchDrawerVisible(false)}>
            <CloseOutlined />
          </button>
        </div>
        <div className="sketch-drawer__body">
          <FormProvider {...sketchForm}>
            <SketchDrawerContent
              state={sketchDrawerState}
              onFinish={handleSketchFormFinish}
              drawingMode={drawingMode}
              setDrawingMode={setDrawingMode}
              resetEntities={() => {
                setSelectedSketchItem(null);
                pointsRef.current = [];
                viewerRef.current?.entities.removeAll();
              }}
              onSaveSketchItem={createNewSketchItem}
              selectedItemType={selectedSketchItem?.type}
              onDeleteItem={deleteItem}
              onSaveConfig={handleSaveSketchItem}
              isCreating={sketchFormMode === 'create'}
            />
          </FormProvider>
        </div>
      </Drawer>

      <LayerPropertiesModal mapId={mapId} setIsLayerLoading={setIsLayerLoading} onLayerUpdateComplete={onLayerUpdateComplete} />

      {saveLayerModalVisible && (
        <SaveLayerModal
          saveLayerModalVisible={saveLayerModalVisible}
          setSaveLayerModalVisible={setSaveLayerModalVisible}
          // #4 — current map, shown first in the post-save "add to a map" step.
          currentMapId={mapId}
          // US-101366 AC-06: after save, refresh the layer list so the new
          // dataset is visible in the File Manager (and so any layer-side
          // metadata stays consistent).
          onSaved={onLayerUpdateComplete}
        />
      )}

      {downloadLayer && (
        <DownloadDatasetModal
          open
          title={`DOWNLOAD LAYER (${downloadLayer.layerTitle || downloadLayer.uniqueLayerName})`}
          onClose={() => setDownloadLayer(null)}
          onDownload={handleDownloadLayer}
        />
      )}

      <DuplicateLayerModal
        open={!!duplicateModalLayer}
        layerName={duplicateModalLayer ?? undefined}
        loading={duplicatePending}
        onClose={() => setDuplicateModalLayer(null)}
        onCreate={async (mode: DuplicateMode) => {
          if (!duplicateModalLayer) return;
          await createDuplicateLayerAsync({ layerName: duplicateModalLayer, mode });
          setDuplicateModalLayer(null);
          // AC-05: refresh so the new "<name> Duplicate" row appears
          // directly below the original in the legend.
          await onLayerUpdateComplete?.();
        }}
      />

      {attributeModalVisible && (
        <ViewAttributeTable
          currentMapId={mapId}
          isVisible={attributeModalVisible}
          // No data passed here, ViewAttributeTable will fetch it
          layerName={selectedLayerName}
          // Prefer live legend title so a rename in Layer Properties is reflected
          // in the table header and download filename without reopening the table.
          layerTitle={drawerItems.find((item) => item.uniqueLayerName === selectedLayerName)?.layerTitle || selectedLayerTitle}
          handleClose={() => setAttributeModalVisible(false)}
          setIsLayerLoading={setIsLayerLoading}
          onLayerUpdateComplete={onLayerUpdateComplete}
          // US-101362 AC-05: hide the edit pencil for Viewers.
          canEdit={canEditLayer}
        />
      )}

      {numericMapId ? (
        <CreateGroupModal
          mapId={numericMapId}
          layer={createGroupForLayer}
          groups={groups}
          onClose={handleCloseCreateGroup}
          onCreated={handleGroupCreated}
        />
      ) : null}

      <Modal
        open={!!pendingTextClick}
        title="Add Text Label"
        okText="Add"
        okButtonProps={{ disabled: !textLabelInput.trim() }}
        onOk={handleConfirmTextLabel}
        onCancel={handleCancelTextLabel}
        maskClosable={false}
        destroyOnClose
      >
        <Input
          autoFocus
          placeholder="Enter label for the text"
          value={textLabelInput}
          onChange={(e) => setTextLabelInput(e.target.value)}
          onPressEnter={handleConfirmTextLabel}
        />
      </Modal>
    </>
  );
};

export default LegendDrawer;
